import dagre from 'dagre'

import type { ProcessStage, ProcessTransition } from './api'

export const STAGE_WIDTH = 210
export const STAGE_HEIGHT = 70

/**
 * Раскладка процесса — dagre, слева направо.
 *
 * Собственный алгоритм, как у оргсхемы (chartLayout.ts), здесь не годится: там дерево,
 * где у каждого узла ровно один родитель, и раскладка сводится к укладке вложенных
 * прямоугольников. Процесс деревом не является — ветки сходятся обратно, встречаются
 * циклы возврата («на доработку»), — и такой граф укладывается послойно, а это и есть
 * задача dagre.
 *
 * Направление LR, а не TB: процесс читается как лента шагов во времени, и то же
 * направление сохранится у нити, где шаги разложены по горизонтали (FR-8a).
 */
export interface PositionedStage {
  stage: ProcessStage
  x: number
  y: number
}

export function layoutProcess(
  stages: ProcessStage[],
  transitions: ProcessTransition[],
): { nodes: PositionedStage[]; width: number; height: number } {
  const graph = new dagre.graphlib.Graph()
  graph.setGraph({ rankdir: 'LR', nodesep: 28, ranksep: 70, marginx: 20, marginy: 20 })
  // Рёбра здесь без собственных данных, но dagre требует функцию по умолчанию.
  graph.setDefaultEdgeLabel(() => ({}))

  for (const stage of stages) {
    graph.setNode(stage.key, { width: STAGE_WIDTH, height: STAGE_HEIGHT })
  }

  const known = new Set(stages.map((stage) => stage.key))
  for (const transition of transitions) {
    // Переход, потерявший узел, dagre молча превратил бы в новый пустой узел —
    // на схеме появился бы призрачный прямоугольник без подписи.
    if (!known.has(transition.from_node_key) || !known.has(transition.to_node_key)) continue
    graph.setEdge(transition.from_node_key, transition.to_node_key)
  }

  dagre.layout(graph)

  const nodes = stages.map((stage) => {
    const placed = graph.node(stage.key)
    return {
      stage,
      // dagre отдаёт центр узла, React Flow ждёт левый верхний угол.
      x: (placed?.x ?? 0) - STAGE_WIDTH / 2,
      y: (placed?.y ?? 0) - STAGE_HEIGHT / 2,
    }
  })

  const size = graph.graph()
  return { nodes, width: size.width ?? 0, height: size.height ?? 0 }
}
